/* Poplar Bluff Trailer — Trailer Finder (v2026-08-12)
 *
 * Four-question quiz (type → use → size → budget) ported from the PBT
 * Conversation Tool. The matching rules are carried over as-is; the data layer
 * is the part that changed: instead of the external inventory feed, products
 * come from the store's own collections via the `collection.finder` template.
 *
 * Because of that, "does this product count as a Gooseneck?" is answered by
 * collection membership rather than a free-text tag. A category's products are
 * fetched the first time the quiz needs them, so a shopper only ever downloads
 * the couple of categories their answers touch.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-tf-main]');
  if (!root) return;

  var configEl = document.querySelector('[data-tf-config]');
  var CONFIG = {};
  try {
    CONFIG = JSON.parse(configEl.textContent);
  } catch (e) {
    CONFIG = {};
  }

  // Every configured category is loadable; only the ones flagged `pick` are
  // offered as answers to question one. Enclosed Car Hauler, for instance, is
  // needed to refine "Car Hauler → enclosed" but isn't its own starting point.
  var TYPE_META = {};
  (CONFIG.types || []).forEach(function (t) {
    TYPE_META[t.value] = t;
  });
  var PICKABLE = (CONFIG.types || []).filter(function (t) { return t.pick !== false; });

  var RESULTS_LIMIT = CONFIG.resultsLimit || 12;

  // Category icons rendered by Liquid from the shared `inventory-icon` snippet.
  var ICONS = {};
  var iconTpl = document.querySelector('[data-tf-icons]');
  if (iconTpl) {
    iconTpl.content.querySelectorAll('[data-tf-icon]').forEach(function (node) {
      ICONS[node.getAttribute('data-tf-icon')] = node.firstElementChild;
    });
  }

  // ======================================================================
  // Quiz options
  // ======================================================================
  var USE_CASES = {
    // Utility is segmented primarily by duty level (GVWR), with two extra paths
    // buyers ask for by name: a tilt-deck option and a motorcycle/trike option.
    // 'heavy' intentionally widens to include Equipment units — see
    // USE_CASE_TYPE_OVERLAP. 'tilt' and 'motorcycle' resolve via category/keyword.
    'Utility Trailer': [
      { value: 'light', label: 'Light duty', hint: 'Single axle · up to 3,500 lbs' },
      { value: 'medium', label: 'Medium duty', hint: 'Tandem axle · 3,500 – 7,000 lbs' },
      { value: 'heavy', label: 'Heavy duty / landscape', hint: 'Tandem axle · 7,000 lbs & up' },
      { value: 'tilt', label: 'Tilt bed', hint: 'Drive-on tilt deck' },
      { value: 'motorcycle', label: 'Motorcycle / trike', hint: 'Aluminum motorcycle & trike haulers' },
      { value: 'general', label: 'Not sure / show all' }
    ],
    'Dump Trailer': [
      { value: 'general', label: 'Dirt, gravel, or debris removal' },
      { value: 'gooseneck', label: 'Heavy-duty dump (gooseneck)' }
    ],
    'Cargo Trailer': [{ value: 'general', label: 'General enclosed cargo' }],
    'Car Hauler': [
      { value: 'general', label: 'Open car hauling' },
      { value: 'enclosed', label: 'Enclosed car transport' },
      { value: 'gooseneck', label: 'Gooseneck car hauler' }
    ],
    'Enclosed Car Hauler': [{ value: 'general', label: 'Enclosed vehicle transport' }],
    'Gooseneck Trailer': [
      { value: 'flatbed', label: 'Flatbed' },
      { value: 'fendered', label: 'Fendered' },
      { value: 'equipment', label: 'Heavy equipment transport' },
      { value: 'tilt', label: 'Gooseneck Tilt' },
      { value: 'hydraulic-dovetail', label: 'Hydraulic Dovetail' },
      { value: 'hotshot', label: 'Hotshot' },
      { value: 'max-ramps', label: 'Max Ramps' },
      { value: 'general', label: 'General Gooseneck' }
    ],
    // Tilt buyers split into two very different groups: the light "utility tilt"
    // customer (all-aluminum units for side-by-sides/ATVs/mowers) and the heavier
    // "equipment tilt / scissor lift hauler" customer. The split runs off GVWR
    // (TILT_GVWR_RANGES) rather than category.
    'Tilt Trailer': [
      { value: 'utility', label: 'Aluminum utility tilt', hint: 'Side-by-side, ATV, mower · up to 10,000 lbs' },
      { value: 'equipment', label: 'Equipment tilt (scissor lift hauler)', hint: 'Skid steers, scissor lifts · 10,001 lbs & up' },
      { value: 'gooseneck', label: 'Gooseneck tilt', hint: 'Tilt deck with a gooseneck hitch' },
      { value: 'general', label: 'Not sure / show all' }
    ],
    // Equipment is segmented by GVWR instead of use case, which gives buyers a
    // clearer sense of the capacity they need.
    'Equipment Trailer': [
      { value: 'gvwr-0-10k', label: 'Up to 10,000 lbs', hint: 'Light-duty · single axle' },
      { value: 'gvwr-10k-14k', label: '10,001 – 14,000 lbs', hint: 'Standard-duty · most common' },
      { value: 'gvwr-14k-18k', label: '14,001 – 18,000 lbs', hint: 'Heavy-duty' },
      { value: 'gvwr-18k-22k', label: '18,001 – 22,000 lbs', hint: 'Extra heavy-duty' },
      { value: 'gvwr-22k-up', label: '22,001 lbs & up', hint: 'Maximum · commercial' },
      { value: 'general', label: 'Not sure / show all sizes' }
    ],
    'Fuel Tank Trailer': [{ value: 'general', label: 'Fuel transport' }],
    'Truck Bed': [{ value: 'general', label: 'Replacement or flatbed truck bed' }]
  };

  var LENGTH_RANGES = [
    { value: '10-14', label: "10' – 14'", min: 10, max: 14 },
    { value: '14-18', label: "14' – 18'", min: 14, max: 18 },
    { value: '18-24', label: "18' – 24'", min: 18, max: 24 },
    { value: '24-30', label: "24' – 30'", min: 24, max: 30 },
    { value: '30+', label: "30' and up", min: 30, max: 999 }
  ];

  var LENGTH_RANGES_BY_TYPE = {
    'Cargo Trailer': [
      { value: '10-12', label: "10' – 12'", min: 10, max: 12 },
      { value: '14', label: "14'", min: 14, max: 14 },
      { value: '16-20', label: "16' – 20'", min: 16, max: 20 },
      { value: '24+', label: "24' and up", min: 24, max: 999 }
    ],
    // Tilts cluster around 16'–18', with 18'–24' being the largest group.
    'Tilt Trailer': [
      { value: '10-16', label: "10' – 16'", min: 10, max: 16 },
      { value: '16-18', label: "16' – 18'", min: 16, max: 18 },
      { value: '18-24', label: "18' – 24'", min: 18, max: 24 },
      { value: '24-30', label: "24' – 30'", min: 24, max: 30 },
      { value: '30+', label: "30' and up", min: 30, max: 999 }
    ]
  };

  function getLengthRanges(type) {
    return LENGTH_RANGES_BY_TYPE[type] || LENGTH_RANGES;
  }

  var GALLON_CAPACITIES = [
    { value: '350', label: '350 Gallon', match: 350 },
    { value: '500', label: '500 Gallon', match: 500 },
    { value: '990', label: '990 Gallon', match: 990 }
  ];

  var TRUCK_BED_FITMENTS = [
    { value: 'Short Bed Single Wheel', label: 'Short Bed Single Wheel' },
    { value: 'Long Bed Dual Wheel', label: 'Long Bed Dual Wheel' }
  ];

  var BUDGET_RANGES = [
    { value: 'under-5k', label: 'Under $5,000', min: 0, max: 5000 },
    { value: '5k-8k', label: '$5,000 – $8,000', min: 5000, max: 8000 },
    { value: '8k-12k', label: '$8,000 – $12,000', min: 8000, max: 12000 },
    { value: '12k-20k', label: '$12,000 – $20,000', min: 12000, max: 20000 },
    { value: 'over-20k', label: '$20,000+', min: 20000, max: Infinity }
  ];

  // Goosenecks are higher-ticket — they rarely sell under ~$10k and regularly
  // reach $30k–$40k+, so the low brackets are dropped and high-end granularity
  // is added.
  var BUDGET_RANGES_BY_TYPE = {
    'Gooseneck Trailer': [
      { value: 'under-10k', label: 'Under $10,000', min: 0, max: 10000 },
      { value: '10k-15k', label: '$10,000 – $15,000', min: 10000, max: 15000 },
      { value: '15k-20k', label: '$15,000 – $20,000', min: 15000, max: 20000 },
      { value: '20k-30k', label: '$20,000 – $30,000', min: 20000, max: 30000 },
      { value: '30k-40k', label: '$30,000 – $40,000', min: 30000, max: 40000 },
      { value: 'over-40k', label: '$40,000+', min: 40000, max: Infinity }
    ]
  };

  function getBudgetRanges(type) {
    return BUDGET_RANGES_BY_TYPE[type] || BUDGET_RANGES;
  }

  // Resolves the selected budget bracket + expansion level into a soft range.
  // Expansion widens the ceiling upward into adjacent brackets so a buyer capped
  // at, say, "$12k–$20k" can pull in a perfect $21k unit on demand. The floor is
  // kept from the originally selected bracket.
  function getEffectiveBudget(type, value, expansion) {
    if (!value) return null;
    var ranges = getBudgetRanges(type);
    var i = -1;
    for (var k = 0; k < ranges.length; k++) {
      if (ranges[k].value === value) { i = k; break; }
    }
    if (i === -1) return null;

    var lastIndex = ranges.length - 1;
    var topIndex = Math.min(i + Math.max(expansion || 0, 0), lastIndex);
    var nextIndex = topIndex + 1;

    return {
      min: ranges[i].min,
      max: ranges[topIndex].max,
      canWiden: nextIndex <= lastIndex,
      nextLabel: nextIndex <= lastIndex ? ranges[nextIndex].label : null
    };
  }

  // Use-case answers that narrow to a second category. In the original these
  // were tag requirements; here they are "must also be in this collection".
  var SECONDARY_TYPE_MAP = {
    // 'light'/'medium'/'heavy' resolve via GVWR; 'tilt' narrows the
    // (Tilt-widened) base back down to genuine tilt decks.
    'Utility Trailer': { tilt: ['Tilt Trailer'] },
    'Dump Trailer': { gooseneck: ['Gooseneck Trailer'] },
    'Car Hauler': {
      enclosed: ['Enclosed Car Hauler'],
      gooseneck: ['Gooseneck Trailer']
    },
    'Gooseneck Trailer': {
      equipment: ['Equipment Trailer'],
      tilt: ['Tilt Trailer']
    },
    'Tilt Trailer': { gooseneck: ['Gooseneck Trailer'] }
  };

  // Distinctions no category captures — matched against the product's title and
  // description text instead.
  var KEYWORD_FILTERS = {
    // Motorcycle/trike haulers have no category of their own; they're Aluma
    // "MC"/"TK" utility units identifiable only by their copy.
    'Utility Trailer': { motorcycle: ['motorcycle', 'trike'] },
    'Gooseneck Trailer': {
      flatbed: ['flatbed', 'flat bed', 'deckover', 'deck over'],
      fendered: ['drive over', 'driveover', 'drive on'],
      'hydraulic-dovetail': ['hydraulic dovetail', 'hydraulic dove tail'],
      hotshot: ['hotshot', 'hot shot'],
      'max-ramps': ['max ramps', 'maxx ramps']
    }
  };

  function getSecondaryTypes(type, useCase) {
    var byType = SECONDARY_TYPE_MAP[type];
    if (!byType || !useCase) return [];
    return byType[useCase] || [];
  }

  function getKeywordFilter(type, useCase) {
    var byType = KEYWORD_FILTERS[type];
    if (!byType || !useCase) return [];
    return byType[useCase] || [];
  }

  function matchesKeywords(p, keywords) {
    if (keywords.length === 0) return true;
    var haystack = (p.hay || p.title || '').toLowerCase();
    return keywords.some(function (kw) {
      return haystack.indexOf(kw) !== -1;
    });
  }

  function getSizeStepMode(type) {
    if (type === 'Fuel Tank Trailer') return 'gallon';
    if (type === 'Truck Bed') return 'fitment';
    return 'length';
  }

  // GVWR (lbs) buckets. Boundary values fall into the lower bracket
  // (14,000 → '10k-14k'; 3,500 → 'light').
  var EQUIPMENT_GVWR_RANGES = {
    'gvwr-0-10k': { min: 0, max: 9999 },
    'gvwr-10k-14k': { min: 10000, max: 14000 },
    'gvwr-14k-18k': { min: 14001, max: 18000 },
    'gvwr-18k-22k': { min: 18001, max: 22000 },
    'gvwr-22k-up': { min: 22001, max: Infinity }
  };
  // All-aluminum utility tilts top out around 9,990; steel equipment /
  // scissor-lift tilts run 10k and up.
  var TILT_GVWR_RANGES = {
    utility: { min: 0, max: 10000 },
    equipment: { min: 10001, max: Infinity }
  };
  var UTILITY_GVWR_RANGES = {
    light: { min: 0, max: 3500 },
    medium: { min: 3501, max: 7000 },
    heavy: { min: 7001, max: Infinity }
  };

  var GVWR_USE_CASE_RANGES = {
    'Equipment Trailer': EQUIPMENT_GVWR_RANGES,
    'Tilt Trailer': TILT_GVWR_RANGES,
    'Utility Trailer': UTILITY_GVWR_RANGES
  };

  function getUseCaseGvwrRange(type, useCase) {
    var byType = GVWR_USE_CASE_RANGES[type];
    if (!byType || !useCase) return null;
    return byType[useCase] || null;
  }

  function matchesGvwrRange(p, range) {
    return p.gvwrLbs != null && p.gvwrLbs >= range.min && p.gvwrLbs <= range.max;
  }

  // Categories that overlap wholesale: enclosed cargo trailers are routinely
  // merchandised as enclosed car haulers too.
  var TYPE_OVERLAP = { 'Cargo Trailer': ['Enclosed Car Hauler'] };

  // Overlaps scoped to a single use-case, so they never pollute the sibling
  // answers or the "show all" view.
  var USE_CASE_TYPE_OVERLAP = {
    'Utility Trailer': {
      heavy: ['Equipment Trailer'],
      tilt: ['Tilt Trailer']
    },
    // Car Haulers is open haulers only since the taxonomy split; the
    // "enclosed" answer widens to the Enclosed Car Hauler collection.
    'Car Hauler': {
      enclosed: ['Enclosed Car Hauler']
    }
  };

  function getUseCaseTypeOverlap(type, useCase) {
    var byType = USE_CASE_TYPE_OVERLAP[type];
    if (!byType || !useCase) return [];
    return byType[useCase] || [];
  }

  // ======================================================================
  // Data layer — one fetch per category, cached for the page's lifetime
  // ======================================================================
  var byId = {};           // product id → normalized product (with .types)
  var typeRequests = {};   // category value → Promise

  function parseNumber(raw) {
    if (raw == null) return null;
    var m = String(raw).replace(/,/g, '').match(/(\d+(?:\.\d+)?)/);
    return m ? parseFloat(m[1]) : null;
  }

  // Length lives in the `pbt.length` metafield ("26 ft", "26'"), but plenty of
  // units only carry it in the title as a deck size ("83X20") or a plain
  // footage mark ("20'"), so both are tried before giving up.
  function parseLengthFeet(raw, title) {
    var v = parseNumber(raw);
    if (v != null) {
      if (/in\b|inch|"/i.test(String(raw)) && v > 60) v = v / 12;
      if (v > 0) return Math.round(v);
    }
    var deck = title.match(/(\d{2,3})\s*[xX×]\s*(\d{1,3})(?!\d)/);
    if (deck) {
      var feet = parseInt(deck[2], 10);
      if (feet > 0 && feet < 100) return feet;
    }
    var mark = title.match(/(\d{1,2})\s*(?:'|ft\b|foot\b|feet\b)/i);
    if (mark) return parseInt(mark[1], 10);
    return null;
  }

  function parseGvwr(raw, haystack) {
    var v = parseNumber(raw);
    if (v != null && v > 0) return v;
    var m = (haystack || '').match(/GVWR:?\s*([\d,]+)/i);
    if (!m) return null;
    var n = parseInt(m[1].replace(/,/g, ''), 10);
    return isFinite(n) && n > 0 ? n : null;
  }

  function normalize(raw) {
    var title = raw.t || '';
    var hay = (raw.hay || title).toLowerCase();

    // Shopify models a markdown as price < compare_at_price. The quiz wants the
    // pair the other way round: `price` is the sticker, `salePrice` is what the
    // shopper actually pays (null when nothing is marked down).
    var current = raw.p || 0;
    var compare = raw.c || 0;
    var onSale = compare > current;

    var gallonCapacity = null;
    var gallon = title.match(/(\d+)\s*gallon/i);
    if (gallon) gallonCapacity = parseInt(gallon[1], 10);

    var fitment = title.match(/(Short Bed Single Wheel|Long Bed Dual Wheel|Long Bed Single Wheel|Short Bed Dual Wheel)/i);

    return {
      id: raw.id,
      title: title,
      hay: hay,
      noPhotos: !!raw.np,
      url: raw.u || '',
      image: raw.img || '',
      imageCount: raw.n || 0,
      price: onSale ? compare : current,
      salePrice: onSale ? current : null,
      brand: raw.br || '',
      category: raw.cat || '',
      condition: raw.cond || '',
      year: raw.yr || '',
      sku: raw.sku || '',
      lengthRaw: raw.len || '',
      gvwrRaw: raw.gvwr || '',
      payloadRaw: raw.pay || '',
      axlesRaw: raw.ax || '',
      features: raw.feat || [],
      lengthFeet: parseLengthFeet(raw.len, title),
      gvwrLbs: parseGvwr(raw.gvwr, hay),
      gallonCapacity: gallonCapacity,
      truckBedFitment: fitment ? fitment[1] : null,
      types: []
    };
  }

  function fetchPage(url, page) {
    var sep = url.indexOf('?') === -1 ? '?' : '&';
    // NO Accept header: `application/json` triggers Shopify's content
    // negotiation, which returns the store's native collection JSON and
    // ignores the finder view entirely (the bug that blanked the quiz).
    return fetch(url + sep + 'view=finder&page=' + page).then(function (res) {
      if (!res.ok) throw new Error('HTTP ' + res.status);
      return res.text();
    }).then(function (text) {
      // The finder view serves JSON with a text/html content type, and
      // theme-preview sessions get Shopify's toolbar script appended to
      // text/html responses — which breaks a naive parse (and blanked the
      // whole quiz for anyone testing through a preview link). Parse just
      // the leading JSON object, string-aware.
      try {
        return JSON.parse(text);
      } catch (err) {
        var start = text.indexOf('{');
        if (start === -1) throw err;
        var depth = 0, inStr = false, esc = false;
        for (var i = start; i < text.length; i++) {
          var ch = text.charAt(i);
          if (esc) { esc = false; continue; }
          if (ch === '\\') { esc = true; continue; }
          if (ch === '"') { inStr = !inStr; continue; }
          if (inStr) continue;
          if (ch === '{') depth++;
          else if (ch === '}') {
            depth--;
            if (depth === 0) return JSON.parse(text.slice(start, i + 1));
          }
        }
        throw err;
      }
    });
  }

  function absorb(payload, typeValue) {
    (payload.products || []).forEach(function (raw) {
      // Sold units stay on collection pages (with the SOLD tape) but the quiz
      // must never recommend one — that's a dead-end match.
      if (raw.sold) return;
      var existing = byId[raw.id];
      if (!existing) {
        existing = normalize(raw);
        byId[raw.id] = existing;
      }
      if (existing.types.indexOf(typeValue) === -1) existing.types.push(typeValue);
    });
  }

  // Page 1 tells us how many more there are; the rest go out together.
  function loadType(typeValue) {
    if (typeRequests[typeValue]) return typeRequests[typeValue];

    var meta = TYPE_META[typeValue];
    if (!meta || !meta.url) {
      typeRequests[typeValue] = Promise.resolve();
      return typeRequests[typeValue];
    }

    typeRequests[typeValue] = fetchPage(meta.url, 1).then(function (first) {
      absorb(first, typeValue);
      var rest = [];
      for (var p = 2; p <= (first.pages || 1); p++) {
        rest.push(fetchPage(meta.url, p).then(function (payload) {
          absorb(payload, typeValue);
        }));
      }
      return Promise.all(rest);
    }).catch(function (err) {
      // Let the request be retried on the next render rather than caching a miss.
      delete typeRequests[typeValue];
      throw err;
    });

    return typeRequests[typeValue];
  }

  // Every category the current answers could need: the chosen one, its blanket
  // overlaps, the use-case widening, and any category used as a secondary
  // narrowing filter (we can only test membership for categories we've loaded).
  function requiredTypes() {
    var t = state.trailerType;
    if (!t) return [];
    var list = [t]
      .concat(TYPE_OVERLAP[t] || [])
      .concat(getUseCaseTypeOverlap(t, state.useCase))
      .concat(getSecondaryTypes(t, state.useCase));
    return list.filter(function (v, i) {
      return list.indexOf(v) === i && TYPE_META[v];
    });
  }

  function ready() {
    return requiredTypes().every(function (t) {
      return typeRequests[t] && typeRequests[t].settled;
    });
  }

  function ensureLoaded() {
    var needed = requiredTypes();
    return Promise.all(needed.map(function (t) {
      var req = loadType(t);
      if (!req.settled) {
        req.then(function () { req.settled = true; }, function () {});
      }
      return req;
    }));
  }

  function allProducts() {
    return Object.keys(byId).map(function (k) { return byId[k]; });
  }

  function hasType(p, t) {
    return p.types.indexOf(t) !== -1;
  }

  // ======================================================================
  // Filtering
  // ======================================================================
  var state = {
    step: 1,
    trailerType: null,
    useCase: null,
    lengthRange: null,
    budgetRange: null,
    budgetExpansion: 0
  };
  var showAllResults = false;

  function filterByType(list) {
    if (!state.trailerType) return list;
    var types = [state.trailerType]
      .concat(TYPE_OVERLAP[state.trailerType] || [])
      .concat(getUseCaseTypeOverlap(state.trailerType, state.useCase));
    return list.filter(function (p) {
      return types.some(function (t) { return hasType(p, t); });
    });
  }

  function filterByUseCase(list) {
    if (!state.trailerType || !state.useCase || state.useCase === 'general') return list;

    // GVWR-segmented answers resolve to a weight range instead of a category.
    var gvwrRange = getUseCaseGvwrRange(state.trailerType, state.useCase);
    if (gvwrRange) {
      return list.filter(function (p) { return matchesGvwrRange(p, gvwrRange); });
    }

    var extra = getSecondaryTypes(state.trailerType, state.useCase);
    var keywords = getKeywordFilter(state.trailerType, state.useCase);
    if (extra.length === 0 && keywords.length === 0) return list;
    return list.filter(function (p) {
      var typesOk = extra.every(function (t) { return hasType(p, t); });
      return typesOk && matchesKeywords(p, keywords);
    });
  }

  function filterBySize(list) {
    if (!state.lengthRange) return list;
    var mode = getSizeStepMode(state.trailerType);

    if (mode === 'gallon') {
      var gallon = GALLON_CAPACITIES.filter(function (g) { return g.value === state.lengthRange; })[0];
      if (!gallon) return list;
      return list.filter(function (p) { return p.gallonCapacity === gallon.match; });
    }
    if (mode === 'fitment') {
      return list.filter(function (p) { return p.truckBedFitment === state.lengthRange; });
    }
    var range = getLengthRanges(state.trailerType).filter(function (r) { return r.value === state.lengthRange; })[0];
    if (!range) return list;
    return list.filter(function (p) {
      if (!p.lengthFeet) return false;
      return p.lengthFeet >= range.min && p.lengthFeet <= range.max;
    });
  }

  function priceOf(p) {
    return p.salePrice != null ? p.salePrice : p.price;
  }

  function filterByBudget(list) {
    if (!state.budgetRange) return list;
    var range = getEffectiveBudget(state.trailerType, state.budgetRange, state.budgetExpansion);
    if (!range) return list;
    return list.filter(function (p) {
      var price = priceOf(p);
      return price >= range.min && price <= range.max;
    });
  }

  function afterUseCase() { return filterByUseCase(filterByType(allProducts())); }
  function afterSize() { return filterBySize(afterUseCase()); }

  function filteredProducts() {
    var results = filterByBudget(afterSize());
    results.sort(function (a, b) {
      // Units still waiting on photos always recommend after photo'd ones.
      var aNp = a.noPhotos ? 1 : 0;
      var bNp = b.noPhotos ? 1 : 0;
      if (aNp !== bNp) return aNp - bNp;
      var aSale = a.salePrice ? 1 : 0;
      var bSale = b.salePrice ? 1 : 0;
      if (bSale !== aSale) return bSale - aSale;
      return priceOf(a) - priceOf(b);
    });
    return results;
  }

  // Counts how many products fall inside a given budget expansion level,
  // starting from the non-budget-filtered base.
  function countAtBudgetExpansion(expansion) {
    var base = afterSize();
    if (!state.budgetRange) return base.length;
    var range = getEffectiveBudget(state.trailerType, state.budgetRange, expansion);
    if (!range) return base.length;
    return base.filter(function (p) {
      var price = priceOf(p);
      return price >= range.min && price <= range.max;
    }).length;
  }

  function getBudgetWiden() {
    var empty = { canWiden: false, nextLabel: null, additionalCount: 0 };
    if (!state.budgetRange) return empty;
    var range = getEffectiveBudget(state.trailerType, state.budgetRange, state.budgetExpansion);
    if (!range || !range.canWiden) return empty;
    return {
      canWiden: true,
      nextLabel: range.nextLabel,
      additionalCount:
        countAtBudgetExpansion(state.budgetExpansion + 1) -
        countAtBudgetExpansion(state.budgetExpansion)
    };
  }

  // ======================================================================
  // DOM helpers
  // ======================================================================
  function el(tag, attrs, children) {
    var node = document.createElement(tag);
    attrs = attrs || {};
    Object.keys(attrs).forEach(function (key) {
      var val = attrs[key];
      if (val == null || val === false) return;
      if (key === 'text') node.textContent = val;
      else if (key === 'html') node.innerHTML = val;
      else if (key === 'onClick') node.addEventListener('click', val);
      else if (key === 'onError') node.addEventListener('error', val);
      else if (val === true) node.setAttribute(key, '');
      else node.setAttribute(key, val);
    });
    if (typeof children === 'string') node.appendChild(document.createTextNode(children));
    else if (Array.isArray(children)) children.forEach(function (c) { if (c) node.appendChild(c); });
    return node;
  }

  function clear(node) {
    while (node.firstChild) node.removeChild(node.firstChild);
  }

  function money(amount) {
    return '$' + Math.round(amount).toLocaleString('en-US');
  }

  // Mirrors the estimate on the inventory cards (assets/collection.js).
  function monthly(principal) {
    var months = CONFIG.term || 60;
    var r = ((CONFIG.apr || 8.99) / 100) / 12;
    if (r === 0) return principal / months;
    return principal * r / (1 - Math.pow(1 + r, -months));
  }

  function icon(paths, extra) {
    var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', extra || '2');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');
    svg.setAttribute('aria-hidden', 'true');
    svg.innerHTML = paths;
    return svg;
  }

  // ======================================================================
  // Chrome — progress, footer
  // ======================================================================
  var STEPS = [
    { num: 1, label: 'Type' },
    { num: 2, label: 'Use' },
    { num: 3, label: 'Size' },
    { num: 4, label: 'Budget' }
  ];

  function renderProgress() {
    var host = document.querySelector('[data-tf-progress]');
    clear(host);

    var steps = el('ol', { class: 'tf-steps' });
    STEPS.forEach(function (s) {
      var done = state.step > s.num;
      var cls = 'tf-step' + (state.step === s.num ? ' is-active' : done ? ' is-done' : '');
      var inner = el('button', {
        type: 'button',
        class: 'tf-step__btn',
        disabled: !done,
        'aria-current': state.step === s.num ? 'step' : null,
        onClick: function () { if (done) goToStep(s.num); }
      }, [
        el('span', { class: 'tf-step__num', text: done ? '✓' : String(s.num) }),
        el('span', { class: 'tf-step__label', text: s.label })
      ]);
      steps.appendChild(el('li', { class: cls }, [inner]));
    });

    var pct = state.step >= 5 ? 100 : ((state.step - 1) / 4) * 100;
    host.appendChild(steps);
    host.appendChild(el('div', { class: 'tf-track' }, [
      el('span', { class: 'tf-track__fill', style: 'width:' + pct + '%' })
    ]));
  }

  function renderFooter() {
    var host = document.querySelector('[data-tf-foot]');
    clear(host);
    if (state.step <= 1 || state.step >= 5) {
      host.hidden = true;
      return;
    }
    host.hidden = false;
    host.appendChild(el('button', {
      type: 'button', class: 'tf-back', onClick: prevStep
    }, [icon('<path d="M19 12H5M12 19l-7-7 7-7"/>', '2.4'), el('span', { text: 'Back' })]));

    host.appendChild(ready()
      ? el('span', { class: 'tf-matchcount', html: '<b>' + filteredProducts().length + '</b> match so far' })
      : el('span', { class: 'tf-matchcount', text: 'Checking stock…' }));

    host.appendChild(el('button', {
      type: 'button', class: 'tf-restart', text: 'Start over', onClick: reset
    }));
  }

  function renderLoading(message) {
    clear(root);
    root.appendChild(el('div', { class: 'tf-loading' }, [
      el('span', { class: 'tf-spinner', 'aria-hidden': 'true' }),
      el('p', { text: message || 'Checking the lot…' })
    ]));
  }

  function renderLoadError() {
    clear(root);
    root.appendChild(el('div', { class: 'empty-state' }, [
      el('h3', { text: "We couldn't load inventory" }),
      el('p', { text: 'Something went wrong on our end. Browse the full lot, or give us a call and we’ll match you by hand.' }),
      el('div', { class: 'tf-actions' }, [
        el('a', { class: 'tf-btn tf-btn--red', href: CONFIG.allUrl || '/collections/all', text: 'Browse inventory' }),
        el('a', { class: 'tf-btn tf-btn--ghost', href: 'tel:' + (CONFIG.phoneHref || ''), text: CONFIG.phone || 'Call us' })
      ])
    ]));
  }

  // ======================================================================
  // Steps
  // ======================================================================
  function stepHead(question, hint) {
    return el('div', { class: 'tf-q' }, [
      el('h2', { class: 'tf-q__title', text: question }),
      hint ? el('p', { class: 'tf-q__hint', text: hint }) : null
    ]);
  }

  function option(opts) {
    var cls = 'tf-opt' +
      (opts.icon ? ' tf-opt--icon' : '') +
      (opts.selected ? ' is-selected' : '') +
      (opts.empty ? ' is-empty' : '');

    var text = el('span', { class: 'tf-opt__txt' }, [
      el('span', { class: 'tf-opt__label', text: opts.label }),
      opts.meta ? el('span', { class: 'tf-opt__meta', text: opts.meta }) : null
    ]);

    return el('button', {
      type: 'button',
      class: cls,
      disabled: opts.empty,
      onClick: opts.empty ? null : opts.onClick
    }, [
      opts.icon ? el('span', { class: 'tf-opt__ic' + (opts.icon.tagName === 'IMG' ? ' tf-opt__ic--photo' : '') }, [opts.icon.cloneNode(true)]) : null,
      text
    ]);
  }

  // --- Step 1: type. Counts come from Liquid, so this renders before any fetch.
  function renderStepType() {
    clear(root);
    var wrap = el('div', { class: 'tf-step-body' }, [
      stepHead('What type of trailer are you looking for?', 'Pick the category that best fits the job.')
    ]);
    var grid = el('div', { class: 'tf-options tf-options--tiles' });
    PICKABLE.forEach(function (t) {
      grid.appendChild(option({
        label: t.label,
        meta: t.count + ' in stock',
        icon: ICONS[t.value],
        selected: state.trailerType === t.value,
        onClick: function () { selectType(t.value); }
      }));
    });
    wrap.appendChild(grid);
    root.appendChild(wrap);
  }

  // --- Step 2: use case (or GVWR, for the weight-segmented categories)
  function renderStepUseCase() {
    clear(root);
    var options = USE_CASES[state.trailerType] || [];
    var isGvwr = options.some(function (uc) { return uc.value.indexOf('gvwr-') === 0; });

    var wrap = el('div', { class: 'tf-step-body' }, [
      stepHead(
        isGvwr ? 'What weight rating (GVWR) do you need?' : 'What will you mainly use it for?',
        isGvwr
          ? 'GVWR is the maximum loaded weight the trailer is rated for. Pick the capacity that covers your heaviest load.'
          : 'This narrows things down to the right configuration.'
      )
    ]);

    var grid = el('div', { class: 'tf-options tf-options--wide' });
    options.forEach(function (uc) {
      grid.appendChild(option({
        label: uc.label,
        meta: uc.hint,
        selected: state.useCase === uc.value,
        onClick: function () { selectUseCase(uc.value); }
      }));
    });
    wrap.appendChild(grid);
    root.appendChild(wrap);
  }

  // --- Step 3: size (length, gallons, or truck-bed fitment)
  function renderStepSize() {
    clear(root);
    var mode = getSizeStepMode(state.trailerType);
    var current = afterUseCase();

    var copy = {
      gallon: ['What tank size do you need?', "Select a capacity — we'll show what's on the lot."],
      fitment: ['What truck bed fitment do you need?', "Select your truck's bed configuration."],
      length: ['What size trailer do you need?', "Select a length range — we'll show what's on the lot."]
    }[mode];

    var wrap = el('div', { class: 'tf-step-body' }, [stepHead(copy[0], copy[1])]);
    var grid = el('div', { class: 'tf-options tf-options--wide' });

    var entries = mode === 'gallon' ? GALLON_CAPACITIES
      : mode === 'fitment' ? TRUCK_BED_FITMENTS
        : getLengthRanges(state.trailerType);

    entries.forEach(function (entry) {
      var count = current.filter(function (p) {
        if (mode === 'gallon') return p.gallonCapacity === entry.match;
        if (mode === 'fitment') return p.truckBedFitment === entry.value;
        return p.lengthFeet != null && p.lengthFeet >= entry.min && p.lengthFeet <= entry.max;
      }).length;

      grid.appendChild(option({
        label: entry.label,
        meta: count + ' available',
        empty: count === 0,
        selected: state.lengthRange === entry.value,
        onClick: function () { selectSize(entry.value); }
      }));
    });

    wrap.appendChild(grid);
    root.appendChild(wrap);
  }

  // --- Step 4: budget
  function renderStepBudget() {
    clear(root);
    var current = afterSize();
    var wrap = el('div', { class: 'tf-step-body' }, [
      stepHead("What's your budget?", "Optional — skip it if you'd rather see everything.")
    ]);
    var grid = el('div', { class: 'tf-options tf-options--wide' });

    getBudgetRanges(state.trailerType).forEach(function (range) {
      var count = current.filter(function (p) {
        var price = priceOf(p);
        return price >= range.min && price <= range.max;
      }).length;

      grid.appendChild(option({
        label: range.label,
        meta: count + ' available',
        empty: count === 0,
        selected: state.budgetRange === range.value,
        onClick: function () { selectBudget(range.value); }
      }));
    });

    grid.appendChild(option({
      label: 'Skip — show all prices',
      onClick: function () { selectBudget(null); }
    }));

    wrap.appendChild(grid);
    root.appendChild(wrap);
  }

  // ======================================================================
  // Results — rendered with the same card markup as the inventory grid, so
  // they inherit assets/collection.css and look like the rest of the site.
  // ======================================================================
  function buildCard(p) {
    var card = el('article', { class: 'card' });

    var badge = p.condition || (p.year ? 'New ' + p.year : '');
    var media = el('a', { class: 'media', href: p.url, 'aria-label': p.title });
    if (badge) media.appendChild(el('span', { class: 'tag', text: badge }));
    if (p.image) {
      media.appendChild(el('img', {
        src: p.image, alt: p.title, loading: 'lazy', width: '520', height: '390'
      }));
    } else {
      media.appendChild(el('span', { class: 'tf-noimg', text: 'Photos coming soon' }));
    }
    if (p.imageCount > 1) {
      var cnt = el('span', { class: 'cnt' });
      cnt.appendChild(icon('<rect x="3" y="6" width="18" height="14" rx="2"/><circle cx="12" cy="13" r="3"/><path d="M8 6V4h8v2"/>'));
      cnt.appendChild(document.createTextNode(String(p.imageCount)));
      media.appendChild(cnt);
    }
    card.appendChild(media);

    var eyebrowText = p.brand + (p.category ? ' · ' + p.category : '');
    var eyebrow = el('div', { class: 'eyebrow' }, [
      el('span', { class: 'dot' }),
      document.createTextNode(eyebrowText)
    ]);
    if (p.sku) eyebrow.appendChild(el('span', { class: 'sku', text: 'Stock #' + p.sku }));

    var title = el('h3', { class: 'ctitle' });
    var link = el('a', { href: p.url });
    if (p.year && p.title.indexOf(p.year) === -1) {
      link.appendChild(el('span', { class: 'yr', text: p.year }));
    }
    link.appendChild(document.createTextNode(p.title));
    title.appendChild(link);

    var specs = el('div', { class: 'specs-inline' });
    [
      ['Length', p.lengthRaw],
      ['GVWR', p.gvwrRaw],
      ['Payload', p.payloadRaw],
      ['Axles', p.axlesRaw]
    ].forEach(function (pair) {
      specs.appendChild(el('div', { class: 's' }, [
        el('div', { class: 'k', text: pair[0] }),
        el('div', { class: 'v', text: pair[1] || '—' })
      ]));
    });

    var body = el('div', { class: 'body' }, [eyebrow, title, specs]);
    if (p.features.length) {
      var feats = el('div', { class: 'features' });
      p.features.slice(0, 5).forEach(function (f) {
        feats.appendChild(el('span', { class: 'f', text: f }));
      });
      body.appendChild(feats);
    }
    card.appendChild(body);

    var pr = el('div', { class: 'pr' });
    if (p.salePrice != null) {
      pr.appendChild(el('span', { class: 'was', text: 'Retail ' + money(p.price) }));
      pr.appendChild(el('span', { class: 'now', text: money(p.salePrice) }));
      pr.appendChild(el('span', { class: 'save', text: '▲ Save ' + money(p.price - p.salePrice) }));
    } else {
      pr.appendChild(el('span', { class: 'now', text: money(p.price) }));
    }

    var pay = el('div', { class: 'pay' }, [
      el('b', { html: '$' + Math.round(monthly(priceOf(p))).toLocaleString('en-US') + '<span style="font-size:11px;font-weight:500">/mo</span>' }),
      el('span', { text: (CONFIG.term || 60) + ' mo · ' + (CONFIG.apr || 8.99) + '% APR' })
    ]);

    var primary = el('a', { class: 'primary', href: p.url }, [
      document.createTextNode(CONFIG.reserveLabel || 'Reserve This Unit')
    ]);
    primary.appendChild(icon('<path d="M5 12h14M13 5l7 7-7 7"/>', '2.4'));

    card.appendChild(el('div', { class: 'buy' }, [
      pr,
      pay,
      primary,
      el('div', { class: 'row2' }, [
        el('a', { href: p.url, text: 'Details' }),
        el('a', { href: p.url + '#product-form', text: 'Financing' })
      ])
    ]));

    return card;
  }

  // The cheapest match next to a near-top pick — the two ends of the range the
  // shopper just described, so the trade-off is visible without scrolling.
  // Below six results the two picks are already most of the grid below, so the
  // comparison just repeats itself — not worth the vertical space.
  function buildComparison(list) {
    if (!CONFIG.showComparison || list.length < 6) return null;

    var sorted = list.slice().sort(function (a, b) { return priceOf(a) - priceOf(b); });
    var value = sorted[0];
    var premium = sorted[Math.max(sorted.length - 3, 1)];
    if (value.id === premium.id) return null;

    var grid = el('div', { class: 'tf-compare__grid' });
    [
      { p: value, badge: 'Best value', cls: 'is-value' },
      { p: premium, badge: 'Premium pick', cls: 'is-premium' }
    ].forEach(function (item) {
      // `.cards.grid-view` gives the card its single-column layout, the same
      // one the inventory grid uses at narrow widths.
      var col = el('div', { class: 'tf-compare__col ' + item.cls }, [
        el('span', { class: 'tf-compare__badge', text: item.badge }),
        el('div', { class: 'cards grid-view' }, [buildCard(item.p)])
      ]);
      grid.appendChild(col);
    });

    return el('div', { class: 'tf-compare' }, [
      el('div', { class: 'tf-compare__head' }, [
        el('h3', { text: 'How they stack up' }),
        el('p', { text: 'The best value in your results next to a step-up pick.' })
      ]),
      grid
    ]);
  }

  function buildWiden(widen) {
    var text = widen.additionalCount > 0
      ? 'There ' + (widen.additionalCount === 1 ? 'is 1 more trailer' : 'are ' + widen.additionalCount + ' more trailers') + ' just outside your range.'
      : 'Expand your budget to see nearby options.';

    return el('div', { class: 'tf-widen' }, [
      el('p', { class: 'tf-widen__text', text: text }),
      el('button', {
        type: 'button',
        class: 'tf-btn tf-btn--ghost',
        text: 'Widen to ' + widen.nextLabel,
        onClick: function () {
          state.budgetExpansion++;
          render();
        }
      })
    ]);
  }

  function renderResults() {
    clear(root);
    var list = filteredProducts();
    var view = el('div', { class: 'tf-results' });

    if (list.length === 0) {
      view.appendChild(el('div', { class: 'empty-state' }, [
        el('h3', { text: 'No exact matches' }),
        el('p', { text: "Nothing on the lot hits every answer right now — widen your budget, change an answer, or let us look for you." }),
        el('div', { class: 'tf-actions' }, [
          el('button', { type: 'button', class: 'tf-btn tf-btn--red', text: 'Start over', onClick: reset }),
          el('a', { class: 'tf-btn tf-btn--ghost', href: typeUrl(), text: 'Browse inventory' }),
          el('a', { class: 'tf-btn tf-btn--ghost', href: 'tel:' + (CONFIG.phoneHref || ''), text: 'Call ' + (CONFIG.phone || '') })
        ])
      ]));
      var widenNone = getBudgetWiden();
      if (widenNone.canWiden) view.appendChild(buildWiden(widenNone));
      root.appendChild(view);
      return;
    }

    var shown = showAllResults ? list.length : Math.min(RESULTS_LIMIT, list.length);
    view.appendChild(el('div', { class: 'tf-results__head' }, [
      el('div', {}, [
        el('h2', { html: 'We found <b>' + list.length + '</b> trailer' + (list.length === 1 ? '' : 's') + ' for you' }),
        el('p', { text: 'Showing ' + (shown === list.length ? 'all ' + list.length : 'the top ' + shown + ' of ' + list.length) + ' matching your answers.' })
      ]),
      el('div', { class: 'tf-actions' }, [
        el('button', { type: 'button', class: 'tf-btn tf-btn--ghost', text: 'Start over', onClick: reset }),
        el('a', { class: 'tf-btn tf-btn--ghost', href: typeUrl(), text: 'Browse all' })
      ])
    ]));

    var comparison = buildComparison(list);
    if (comparison) view.appendChild(comparison);

    var cards = el('div', { class: 'cards grid-view' });
    list.slice(0, shown).forEach(function (p) { cards.appendChild(buildCard(p)); });
    if (comparison) {
      view.appendChild(el('div', { class: 'tf-divider' }, [el('span', { text: 'All results' })]));
    }
    view.appendChild(cards);

    if (list.length > shown) {
      view.appendChild(el('div', { class: 'tf-actions tf-actions--center' }, [
        el('button', {
          type: 'button', class: 'tf-btn tf-btn--ghost',
          text: 'Show all ' + list.length + ' results',
          onClick: function () { showAllResults = true; render(); }
        })
      ]));
    }

    var widen = getBudgetWiden();
    if (widen.canWiden) view.appendChild(buildWiden(widen));

    root.appendChild(view);
  }

  function typeUrl() {
    var meta = TYPE_META[state.trailerType];
    return (meta && meta.url) || CONFIG.allUrl || '/collections/all';
  }

  // ======================================================================
  // Controller
  // ======================================================================
  var renderToken = 0;

  function render() {
    renderProgress();

    if (state.step === 1) {
      renderStep1AndChrome();
      return;
    }

    var token = ++renderToken;
    if (!ready()) renderLoading();

    ensureLoaded().then(function () {
      if (token !== renderToken) return;
      paintStep();
    }, function () {
      if (token !== renderToken) return;
      renderLoadError();
      renderFooter();
    });

    // Already-resolved categories repaint synchronously on the microtask above;
    // the footer is drawn either way so the chrome never flickers empty.
    renderFooter();
  }

  function renderStep1AndChrome() {
    renderStepType();
    renderFooter();
  }

  function paintStep() {
    if (state.step === 2) renderStepUseCase();
    else if (state.step === 3) renderStepSize();
    else if (state.step === 4) renderStepBudget();
    else renderResults();
    renderFooter();
  }

  function scrollIntoView() {
    var panel = document.querySelector('.tf-panel');
    if (!panel) return;
    var top = panel.getBoundingClientRect().top + window.pageYOffset - 90;
    if (window.pageYOffset > top) window.scrollTo({ top: top, behavior: 'smooth' });
  }

  function nextStep() {
    if (state.step < 5) state.step++;
    render();
    scrollIntoView();
  }

  function prevStep() {
    if (state.step > 1) state.step--;
    render();
  }

  function goToStep(step) {
    state.step = step;
    render();
  }

  function reset() {
    state = {
      step: 1, trailerType: null, useCase: null,
      lengthRange: null, budgetRange: null, budgetExpansion: 0
    };
    showAllResults = false;
    render();
    scrollIntoView();
  }

  function selectType(value) {
    state.trailerType = value;
    state.useCase = null;
    state.lengthRange = null;
    state.budgetRange = null;
    state.budgetExpansion = 0;
    nextStep();
  }

  function selectUseCase(value) {
    state.useCase = value;
    state.lengthRange = null;
    state.budgetRange = null;
    state.budgetExpansion = 0;
    nextStep();
  }

  function selectSize(value) {
    state.lengthRange = value;
    state.budgetRange = null;
    state.budgetExpansion = 0;
    nextStep();
  }

  function selectBudget(value) {
    state.budgetRange = value;
    state.budgetExpansion = 0;
    showAllResults = false;
    nextStep();
  }

  render();
})();
